// Multi-user authentication client. Stores a session token + the current user
// in localStorage; every authenticated request sends the token as a Bearer.
import { API_BASE_URL } from '../config';

export type Role = 'admin' | 'user' | 'paper_reviewer';

export interface AuthUser {
  id: string;
  username: string;
  role: Role;
  created_at?: string | null;
}

const TOKEN_KEY = 'racing_ui_token';
const USER_KEY = 'racing_ui_user';

export const getToken = (): string => {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
};

export const getStoredUser = (): AuthUser | null => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
};

const persist = (token: string, user: AuthUser) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    // ignore storage errors in restricted browser modes
  }
};

const clear = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    // ignore
  }
};

// Authorization header for fetch calls (empty object when logged out).
export const authHeader = (): Record<string, string> => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const SESSION_EXPIRED_EVENT = 'betbuilder:session-expired';

// A late response from a previous login must never clear a newer session.
export const expireSession = (authorization?: string): void => {
  const token = getToken();
  if (!token || authorization !== `Bearer ${token}`) return;
  clear();
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
};

// The security gateway can return HTML for 401/403/5xx. Check HTTP status and
// content type before parsing; this helper never resubmits an order or a save.
export const readApiJson = async (res: Response, authorization?: string): Promise<any> => {
  if (!res.headers.get('content-type')?.toLowerCase().includes('application/json')) {
    // Owner 2026-10-29: the nginx session guard answers an expired/revoked token with a bare 401 (no JSON).
    // That is a dead session, not a glitch - sign out so the login screen shows instead of a stuck page.
    if (res.status === 401) {
      expireSession(authorization);
      throw new Error('Your session has expired. Please sign in again.');
    }
    if (res.status === 403) throw new Error('Your account does not have access to this page.');
    if (res.status >= 500) throw new Error(`Server temporarily unavailable (HTTP ${res.status}). Refresh to check the latest status.`);
    throw new Error(`Unexpected server response (HTTP ${res.status}). Please refresh.`);
  }
  let data: any;
  try { data = await res.json(); }
  catch { throw new Error('Invalid data received from the server. Please refresh.'); }
  if (res.status === 401 && data?.error === 'Authentication required') {
    expireSession(authorization);
    throw new Error('Your session is no longer valid. Please sign in again.');
  }
  if (!res.ok) throw new Error(data?.error || `Request failed (HTTP ${res.status}).`);
  return data;
};

const errorFrom = async (res: Response, fallback: string): Promise<string> => {
  try {
    const data = await res.json();
    return data?.error || fallback;
  } catch {
    return fallback;
  }
};

export const login = async (username: string, password: string): Promise<AuthUser> => {
  const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) throw new Error(await errorFrom(res, 'Login failed'));
  const data = await res.json();
  persist(data.token, data.user);
  return data.user;
};

export const logout = async (): Promise<void> => {
  try {
    await fetch(`${API_BASE_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...authHeader() },
    });
  } catch {
    // best-effort server-side invalidation
  }
  clear();
};

// A 30-day HttpOnly cookie can recover the Bearer token if iOS clears web storage.
const restoreSession = async (): Promise<AuthUser | null> => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/auth/restore`, {
      credentials: 'include', cache: 'no-store', signal: AbortSignal.timeout(8000),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.token || !data?.user) return null;
    persist(data.token, data.user);
    return data.user as AuthUser;
  } catch { return null; }
};

// Validate the local token, preserving it through temporary network or gateway errors.
export const verifySession = async (): Promise<AuthUser | null> => {
  const token = getToken();
  if (!token) return restoreSession();
  try {
    const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }, cache: 'no-store', signal: AbortSignal.timeout(8000),
    });
    if (res.status === 401) { // owner 2026-10-29: JSON or bare gateway 401 - either way the token is dead
      clear();
      return restoreSession();
    }
    if (!res.ok) return getStoredUser();
    const data = await res.json();
    persist(token, data.user);
    // Older signed-in devices receive their recovery cookie without another login.
    try {
      await fetch(`${API_BASE_URL}/api/auth/remember`, {
        method: 'POST', credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* The local session remains valid. */ }
    return data.user as AuthUser;
  } catch {
    return getStoredUser();
  }
};

// ── Admin user management ──────────────────────────────────────────────────
export const listUsers = async (): Promise<AuthUser[]> => {
  const res = await fetch(`${API_BASE_URL}/api/auth/users`, { headers: { ...authHeader() } });
  if (!res.ok) throw new Error(await errorFrom(res, 'Failed to load users'));
  return res.json();
};

export const createUser = async (
  username: string,
  password: string,
  role: Role
): Promise<AuthUser> => {
  const res = await fetch(`${API_BASE_URL}/api/auth/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ username, password, role }),
  });
  if (!res.ok) throw new Error(await errorFrom(res, 'Failed to create user'));
  return res.json();
};

export const deleteUser = async (id: string): Promise<void> => {
  const res = await fetch(`${API_BASE_URL}/api/auth/users/${id}`, {
    method: 'DELETE',
    headers: { ...authHeader() },
  });
  if (!res.ok) throw new Error(await errorFrom(res, 'Failed to delete user'));
};
